import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, ZoomControl, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MAP_TILE_URL, MAP_ATTRIBUTION } from 'constants/mapConfig';

// ── Custom pin icon ───────────────────────────────────────────────────────────

const PIN_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
  <filter id="shadow" x="-30%" y="-20%" width="160%" height="160%">
    <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.25)"/>
  </filter>
  <g filter="url(#shadow)">
    <path d="M16 2C9.373 2 4 7.373 4 14c0 9 12 26 12 26s12-17 12-26C28 7.373 22.627 2 16 2z"
      fill="#FFB800" stroke="#e6a800" stroke-width="1.5"/>
    <circle cx="16" cy="14" r="5" fill="white" opacity="0.9"/>
  </g>
</svg>`;

const customIcon = L.divIcon({
  html: PIN_SVG,
  className: '',
  iconSize: [32, 42],
  iconAnchor: [16, 42],
  popupAnchor: [0, -42],
});

// ── Map fly-to ────────────────────────────────────────────────────────────────

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, 16, { duration: 1.2 });
  }, [target, map]);
  return null;
}

// ── Click handler ─────────────────────────────────────────────────────────────

function MapClickHandler({ onLocationChange }) {
  useMapEvents({
    click(e) { onLocationChange(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

// ── MapPicker ─────────────────────────────────────────────────────────────────

export function MapPicker({
  initialLat = -0.1807,
  initialLng = -78.4678,
  onLocationSelect,
  height = '400px',
  flyTo = null,
}) {
  const [position, setPosition] = useState([initialLat, initialLng]);
  const [flyTarget, setFlyTarget] = useState(null);
  const markerRef = useRef(null);
  const flyToRef = useRef(null);

  const handleLocationChange = useCallback((lat, lng) => {
    setPosition([lat, lng]);
    if (onLocationSelect) onLocationSelect(lat, lng);
  }, [onLocationSelect]);

  useEffect(() => {
    if (!flyTo || flyTo === flyToRef.current) return;
    flyToRef.current = flyTo;
    handleLocationChange(flyTo[0], flyTo[1]);
    setFlyTarget([...flyTo]);
  }, [flyTo, handleLocationChange]);

  const eventHandlers = useMemo(() => ({
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const { lat, lng } = marker.getLatLng();
        handleLocationChange(lat, lng);
      }
    },
  }), [handleLocationChange]);

  return (
    <div style={{ width: '100%', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <MapContainer center={[initialLat, initialLng]} zoom={14} style={{ height, width: '100%' }} zoomControl={false} maxBounds={[[-5.5, -81.5], [2.0, -74.5]]} maxBoundsViscosity={1.0} minZoom={6}>
          <TileLayer
            attribution={MAP_ATTRIBUTION}
            url={MAP_TILE_URL}
            subdomains="abcd"
            maxZoom={19}
          />
          <Marker
            draggable
            eventHandlers={eventHandlers}
            position={position}
            ref={markerRef}
            icon={customIcon}
          />
          <ZoomControl position="bottomright" />
          <MapClickHandler onLocationChange={handleLocationChange} />
          <FlyTo target={flyTarget} />
        </MapContainer>
      </div>

      <div style={ss.coords}>
        <a
          href={`https://www.google.com/maps?q=${position[0].toFixed(6)},${position[1].toFixed(6)}`}
          target="_blank"
          rel="noreferrer"
          style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="#2563eb" stroke="none">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          Ver en Maps
        </a>
        <span style={{ color: '#cbd5e1', margin: '0 6px' }}>·</span>
        <span style={{ color: '#9ca3af' }}>Clic en mapa o arrastra el pin para ajustar</span>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const ss = {
  coords: {
    display: 'flex', alignItems: 'center', gap: 5,
    marginTop: 6, fontSize: '0.74rem', color: '#6b7280',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  },
};
